/**
 * RequestStatusCountsBadges.tsx
 *
 * Strip di chip cliccabili che mostrano il conteggio di richieste per stato
 * all'interno di una tipologia (Bozzetto/Campionatura/Produzione/Gamma).
 * Click su un chip applica il filtro di stato. Click su un chip già attivo lo rimuove.
 */

import React, { useMemo } from 'react';
import { Group, Badge, Skeleton } from '@mantine/core';
import { RequestAggregates, RequestStatus, RequestType } from '../../../types/api';

interface RequestStatusCountsBadgesProps {
  type: RequestType;
  aggregates?: RequestAggregates;
  loading?: boolean;
  activeStatus?: RequestStatus;
  onStatusToggle: (status?: RequestStatus) => void;
  visibleStatuses?: RequestStatus[];
}

const STATUS_META: Record<RequestStatus, { label: string; color: string }> = {
  [RequestStatus.New]: { label: 'Nuove', color: 'blue' },
  [RequestStatus.PreCampionatura]: { label: 'In Pre-Campionatura', color: 'grape' },
  [RequestStatus.PreCampionaturaCompleted]: { label: 'Da avviare (Campione)', color: 'indigo' },
  [RequestStatus.InProgress]: { label: 'In Lavorazione', color: 'cyan' },
  [RequestStatus.PendingCompletion]: { label: 'In Attesa', color: 'yellow' },
  [RequestStatus.Completed]: { label: 'Completate', color: 'green' },
  [RequestStatus.Cancelled]: { label: 'Annullate', color: 'red' },
  [RequestStatus.Suspended]: { label: 'Sospese', color: 'gray' },
};

const DEFAULT_VISIBLE: RequestStatus[] = [
  RequestStatus.New,
  RequestStatus.InProgress,
  RequestStatus.PendingCompletion,
  RequestStatus.Suspended,
  RequestStatus.Completed,
  RequestStatus.Cancelled,
];

export function RequestStatusCountsBadges({
  type,
  aggregates,
  loading,
  activeStatus,
  onStatusToggle,
  visibleStatuses = DEFAULT_VISIBLE,
}: RequestStatusCountsBadgesProps) {
  const counts = useMemo(() => {
    const map = new Map<RequestStatus, number>();
    visibleStatuses.forEach((s) => map.set(s, 0));
    aggregates?.byTypeAndStatus
      .filter((b) => b.type === type)
      .forEach((b) => {
        map.set(b.status, (map.get(b.status) ?? 0) + b.count);
      });
    return map;
  }, [aggregates, type, visibleStatuses]);

  if (loading && !aggregates) {
    return (
      <Group gap="xs">
        {visibleStatuses.map((s) => (
          <Skeleton key={s} height={26} width={120} radius="xl" />
        ))}
      </Group>
    );
  }

  return (
    <Group gap="xs" wrap="wrap">
      {visibleStatuses.map((status) => {
        const meta = STATUS_META[status];
        const count = counts.get(status) ?? 0;
        const isActive = activeStatus === status;
        return (
          <Badge
            key={status}
            color={meta.color}
            variant={isActive ? 'filled' : 'light'}
            size="lg"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            role="button"
            aria-pressed={isActive}
            onClick={() => onStatusToggle(isActive ? undefined : status)}
          >
            {meta.label} ({count})
          </Badge>
        );
      })}
    </Group>
  );
}
